import { mediaSource } from '../shared/mediaSource';
import MediaImage from './MediaImage';
// THIS IS OLD OBSOLETE CODE, ONLY USE FOR REFERENCE IN PLAY2.JS REBUILD  *** DO NOT CALL DIRECTLY.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cdoAI from '../cdo/cdoAI.js';
import cdoBeing from '../cdo/cdoBeing.js';
import cdoEvent from '../cdo/cdoEvent.js';
import cdoPlace from '../cdo/cdoPlace.js';

const createEmptyGraph = () => ({
  rootPlaceId: '',
  placesById: {},
  beingsById: {},
  itemsById: {},
});

const createEmptyImageState = () => ({
  src: '',
  revisedPrompt: '',
  isLoading: false,
  isLoadingSavedImage: false,
  isSaving: false,
  isSaved: false,
  hasCheckedSavedImage: false,
  error: '',
});

const createEmptyInteractionState = () => ({
  sessionKey: '',
  started: false,
  messages: [],
  draft: '',
  isLoading: false,
  error: '',
  playerLocation: null,
  playerPath: [],
  visibilitySnapshot: null,
  actionComplete: true,
  pendingActionSummary: '',
});

const PLAN_VIEW_SIZE = 720;
const PLAN_VIEW_MIN_ZOOM = 0.5;
const PLAN_VIEW_MAX_ZOOM = 3;
const PLAN_VIEW_ZOOM_STEP = 0.25;
const PLAN_VIEW_TYPE_COLORS = [
  { fill: '#d9ecff', stroke: '#3b82c4' },
  { fill: '#e5f6df', stroke: '#5d9d44' },
  { fill: '#fbe5d6', stroke: '#c77f3f' },
  { fill: '#f3e4ff', stroke: '#8a63c7' },
  { fill: '#ffe2e0', stroke: '#d26767' },
  { fill: '#efe8d5', stroke: '#8e7a49' },
  { fill: '#dff3f3', stroke: '#4d9696' },
  { fill: '#f7e3ef', stroke: '#b45e8e' },
];

const appendUniqueId = (values, nextValue) => {
  if (!nextValue) {
    return Array.isArray(values) ? values : [];
  }

  const normalizedValues = Array.isArray(values) ? values : [];
  return normalizedValues.includes(nextValue) ? normalizedValues : [...normalizedValues, nextValue];
};

const formatErrorMessage = (err, response) => {
  const source = response?.error ?? response ?? err;

  if (typeof source === 'string') {
    return source;
  }

  if (typeof source === 'number') {
    return `Request failed with status ${source}.`;
  }

  if (!source || typeof source !== 'object') {
    return 'Request failed.';
  }

  const parts = [];
  if (source.stage) {
    parts.push(`Stage: ${source.stage}`);
  }
  if (source.code) {
    parts.push(`Code: ${source.code}`);
  }
  if (source.message) {
    parts.push(source.message);
  } else if (source.sqlMessage) {
    parts.push(source.sqlMessage);
  } else if (source.error && typeof source.error === 'string') {
    parts.push(source.error);
  }

  if (parts.length > 0) {
    return parts.join(' | ');
  }

  try {
    return JSON.stringify(source);
  } catch {
    return 'Request failed.';
  }
};

const callCdo = (fn, ...args) =>
  new Promise((resolve, reject) => {
    fn(...args, (err, response) => {
      if (err || response?.error) {
        reject(new Error(formatErrorMessage(err, response)));
        return;
      }

      resolve(response);
    });
  });

const getPlaceLabel = (place) =>
  place?.name || place?.title || place?.entityName || place?.entityTitle || place?.idEntity || 'Unnamed place';

const getBeingLabel = (being) =>
  being?.name || being?.title || being?.entityName || being?.entityTitle || being?.idEntity || 'Unnamed being';

const getItemLabel = (item) =>
  item?.name || item?.title || item?.entityName || item?.entityTitle || item?.idEntity || 'Unnamed item';

const formatGpValue = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return '0 gp';
  }

  return `${parsed.toLocaleString(undefined, { maximumFractionDigits: 4 })} gp`;
};

const trimText = (value, maxLength = 280) => {
  const normalized = String(value ?? '').trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}...`;
};

const normalizePlanCategoryKey = (value) => String(value ?? '').trim().toLowerCase() || 'unknown';

const getPlanCategoryColor = (category) => {
  const normalized = normalizePlanCategoryKey(category);
  let hash = 0;

  for (let index = 0; index < normalized.length; index += 1) {
    hash = (hash * 31 + normalized.charCodeAt(index)) >>> 0;
  }

  return PLAN_VIEW_TYPE_COLORS[hash % PLAN_VIEW_TYPE_COLORS.length];
};

const resolvePlanCategory = (place) => {
  const zoneType = String(place?.zoneType ?? '').trim();
  if (zoneType) {
    return zoneType;
  }

  const buildingType = String(place?.buildingType ?? '').trim().toLowerCase();
  const placeType = String(place?.placeType ?? '').trim().toLowerCase();

  if (
    buildingType.includes('inn') ||
    buildingType.includes('tavern') ||
    buildingType.includes('store') ||
    buildingType.includes('market') ||
    buildingType.includes('shop') ||
    buildingType.includes('trading')
  ) {
    return 'Commercial';
  }

  if (
    buildingType.includes('forge') ||
    buildingType.includes('foundry') ||
    buildingType.includes('mill') ||
    buildingType.includes('brewery') ||
    buildingType.includes('boatyard') ||
    buildingType.includes('workshop') ||
    buildingType.includes('warehouse')
  ) {
    return 'Industrial';
  }

  if (
    buildingType.includes('temple') ||
    buildingType.includes('chapel') ||
    buildingType.includes('shrine') ||
    buildingType.includes('monastery') ||
    placeType.includes('sacred')
  ) {
    return 'Religious';
  }

  if (
    buildingType.includes('guardhouse') ||
    buildingType.includes('barracks') ||
    buildingType.includes('gatehouse') ||
    buildingType.includes('watchtower') ||
    placeType.includes('fortification')
  ) {
    return 'Military';
  }

  if (
    buildingType.includes('manor') ||
    buildingType.includes('villa') ||
    buildingType.includes('longhouse') ||
    buildingType.includes('tenement') ||
    buildingType.includes('boarding') ||
    buildingType.includes('house')
  ) {
    return 'Residential';
  }

  if (placeType.includes('district')) {
    return 'Mixed';
  }

  return String(place?.placeType ?? '').trim() || 'Unknown';
};

const mergePlace = (currentGraph, place, options = {}) => {
  const placeId = place?.idEntity;
  if (!placeId) {
    return currentGraph;
  }

  const existingPlace = currentGraph.placesById[placeId] ?? {
    childPlaceIds: [],
    inhabitantIds: [],
    itemIds: [],
    parentPlaceId: '',
  };

  const nextPlacesById = {
    ...currentGraph.placesById,
    [placeId]: {
      ...existingPlace,
      idEntity: placeId,
      name: place?.name ?? place?.entityName ?? existingPlace.name ?? '',
      title: place?.title ?? place?.entityTitle ?? existingPlace.title ?? '',
      description: place?.description ?? place?.entityDesc ?? existingPlace.description ?? '',
      level: place?.level ?? existingPlace.level ?? '',
      placeType: place?.placeType ?? existingPlace.placeType ?? '',
      zoneType: place?.zoneType ?? existingPlace.zoneType ?? '',
      buildingType: place?.buildingType ?? existingPlace.buildingType ?? '',
      transitPointType: place?.transitPointType ?? existingPlace.transitPointType ?? '',
      location: place?.location ?? existingPlace.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 },
      shape:
        place?.shape ??
        existingPlace.shape ?? {
          idEntityShape: '',
          shapeType: 'Box',
          minX: 0,
          maxX: 0,
          minY: 0,
          maxY: 0,
          minZ: 0,
          maxZ: 0,
          shapeData: {},
        },
      childPlaceIds: Array.isArray(existingPlace.childPlaceIds) ? existingPlace.childPlaceIds : [],
      inhabitantIds: Array.isArray(existingPlace.inhabitantIds) ? existingPlace.inhabitantIds : [],
      itemIds: Array.isArray(existingPlace.itemIds) ? existingPlace.itemIds : [],
      parentPlaceId: options.parentPlaceId ?? existingPlace.parentPlaceId ?? '',
      currencies: Array.isArray(place?.currencies)
        ? place.currencies.map((currency) => ({
            idEntity: currency?.idEntity ?? placeId,
            idCurrency: currency?.idCurrency ?? '',
            currencyName: currency?.currencyName ?? currency?.name ?? '',
            value: Number(currency?.value) || 0,
            quantity: Number(currency?.quantity) || 0,
          }))
        : Array.isArray(existingPlace.currencies)
          ? existingPlace.currencies
          : [],
    },
  };

  if (options.parentPlaceId) {
    const parentPlace = nextPlacesById[options.parentPlaceId] ?? {
      idEntity: options.parentPlaceId,
      childPlaceIds: [],
      inhabitantIds: [],
      itemIds: [],
    };

    nextPlacesById[options.parentPlaceId] = {
      ...parentPlace,
      childPlaceIds: appendUniqueId(parentPlace.childPlaceIds, placeId),
    };
  }

  return {
    ...currentGraph,
    rootPlaceId: options.setAsRoot ? placeId : currentGraph.rootPlaceId,
    placesById: nextPlacesById,
  };
};

const mergeItem = (currentGraph, item, options = {}) => {
  const itemId = item?.idEntity;
  if (!itemId) {
    return currentGraph;
  }

  const existingItem = currentGraph.itemsById[itemId] ?? {};
  const nextItemsById = {
    ...currentGraph.itemsById,
    [itemId]: {
      ...existingItem,
      idEntity: itemId,
      name: item?.name ?? item?.entityName ?? existingItem.name ?? '',
      title: item?.title ?? item?.entityTitle ?? existingItem.title ?? '',
      description: item?.description ?? item?.entityDesc ?? existingItem.description ?? '',
      level: item?.level ?? existingItem.level ?? '',
      itemType: item?.itemType ?? existingItem.itemType ?? '',
      itemTypeName: item?.itemTypeName ?? existingItem.itemTypeName ?? '',
      stockCategory: item?.stockCategory ?? existingItem.stockCategory ?? '',
      stockDisplayName: item?.stockDisplayName ?? existingItem.stockDisplayName ?? '',
      qualityLevel: item?.qualityLevel ?? existingItem.qualityLevel ?? 0,
      magicLevel: item?.magicLevel ?? existingItem.magicLevel ?? 0,
      weapon: item?.weapon ?? existingItem.weapon ?? '',
      worn: item?.worn ?? existingItem.worn ?? '',
      general: item?.general ?? existingItem.general ?? '',
      container: item?.container ?? existingItem.container ?? '',
      provisionMaterial: item?.provisionMaterial ?? existingItem.provisionMaterial ?? '',
      material: item?.material ?? existingItem.material ?? '',
      quantity: item?.quantity ?? existingItem.quantity ?? null,
      capacity: item?.capacity ?? existingItem.capacity ?? null,
      volume: item?.volume ?? existingItem.volume ?? null,
      length: Number.isFinite(Number(item?.length)) ? Number(item.length) : Number(existingItem.length) || 0,
      weight: Number.isFinite(Number(item?.weight)) ? Number(item.weight) : Number(existingItem.weight) || 0,
      height: Number.isFinite(Number(item?.height)) ? Number(item.height) : Number(existingItem.height) || 0,
      width: Number.isFinite(Number(item?.width)) ? Number(item.width) : Number(existingItem.width) || 0,
      value: Number.isFinite(Number(item?.value)) ? Number(item.value) : Number(existingItem.value) || 0,
      ownerType: options.ownerType ?? existingItem.ownerType ?? '',
      ownerId: options.ownerId ?? existingItem.ownerId ?? '',
    },
  };

  const nextGraph = {
    ...currentGraph,
    itemsById: nextItemsById,
  };

  if (options.ownerType === 'place' && options.ownerId) {
    const ownerPlace = nextGraph.placesById[options.ownerId] ?? {
      idEntity: options.ownerId,
      childPlaceIds: [],
      inhabitantIds: [],
      itemIds: [],
    };

    nextGraph.placesById = {
      ...nextGraph.placesById,
      [options.ownerId]: {
        ...ownerPlace,
        itemIds: appendUniqueId(ownerPlace.itemIds, itemId),
      },
    };
  }

  if (options.ownerType === 'being' && options.ownerId) {
    const ownerBeing = nextGraph.beingsById[options.ownerId] ?? {
      idEntity: options.ownerId,
      itemIds: [],
    };

    nextGraph.beingsById = {
      ...nextGraph.beingsById,
      [options.ownerId]: {
        ...ownerBeing,
        itemIds: appendUniqueId(ownerBeing.itemIds, itemId),
      },
    };
  }

  return nextGraph;
};

const mergeBeing = (currentGraph, being, options = {}) => {
  const beingId = being?.idEntity;
  if (!beingId) {
    return currentGraph;
  }

  const existingBeing = currentGraph.beingsById[beingId] ?? {
    itemIds: [],
    placeId: '',
    relationships: [],
  };

  const nextBeingsById = {
    ...currentGraph.beingsById,
    [beingId]: {
      ...existingBeing,
      idEntity: beingId,
      name: being?.name ?? being?.entityName ?? existingBeing.name ?? '',
      title: being?.title ?? being?.entityTitle ?? existingBeing.title ?? '',
      description: being?.description ?? being?.entityDesc ?? existingBeing.description ?? '',
      level: being?.level ?? existingBeing.level ?? '',
      beingType: being?.beingType ?? existingBeing.beingType ?? '',
      alignment: being?.alignment ?? existingBeing.alignment ?? '',
      personality: being?.personality ?? existingBeing.personality ?? '',
      objectivesShortTerm: being?.objectivesShortTerm ?? existingBeing.objectivesShortTerm ?? '',
      objectivesLongTerm: being?.objectivesLongTerm ?? existingBeing.objectivesLongTerm ?? '',
      strength: being?.strength ?? existingBeing.strength ?? 0,
      agility: being?.agility ?? existingBeing.agility ?? 0,
      constitution: being?.constitution ?? existingBeing.constitution ?? 0,
      intellect: being?.intellect ?? existingBeing.intellect ?? 0,
      wisdom: being?.wisdom ?? existingBeing.wisdom ?? 0,
      charisma: being?.charisma ?? existingBeing.charisma ?? 0,
      spells: Array.isArray(being?.spells) ? being.spells : Array.isArray(existingBeing.spells) ? existingBeing.spells : [],
      currencies: Array.isArray(being?.currencies)
        ? being.currencies.map((currency) => ({
            idEntity: currency?.idEntity ?? beingId,
            idCurrency: currency?.idCurrency ?? '',
            currencyName: currency?.currencyName ?? currency?.name ?? '',
            value: Number(currency?.value) || 0,
            quantity: Number(currency?.quantity) || 0,
          }))
        : Array.isArray(existingBeing.currencies)
          ? existingBeing.currencies
          : [],
      itemIds: Array.isArray(existingBeing.itemIds) ? existingBeing.itemIds : [],
      placeId: options.placeId ?? existingBeing.placeId ?? '',
      location: being?.location ?? existingBeing.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 },
      relationships: Array.isArray(being?.relationships)
        ? being.relationships.map((relationship) => ({
            idEntity: relationship?.idEntity ?? '',
            idRelated: relationship?.idRelated ?? '',
            direction: relationship?.direction ?? '',
            relateType: relationship?.relateType ?? '',
            otherEntityId: relationship?.otherEntityId ?? '',
            otherEntityName: relationship?.otherEntityName ?? '',
            otherEntityTitle: relationship?.otherEntityTitle ?? '',
            otherEntityType: relationship?.otherEntityType ?? '',
            score: Number.isFinite(Number(relationship?.score)) ? Number(relationship.score) : 50,
            demeanor: relationship?.demeanor ?? '',
          }))
        : Array.isArray(existingBeing.relationships)
          ? existingBeing.relationships
          : [],
    },
  };

  const nextGraph = {
    ...currentGraph,
    beingsById: nextBeingsById,
  };

  if (options.placeId) {
    const parentPlace = nextGraph.placesById[options.placeId] ?? {
      idEntity: options.placeId,
      childPlaceIds: [],
      inhabitantIds: [],
      itemIds: [],
    };

    nextGraph.placesById = {
      ...nextGraph.placesById,
      [options.placeId]: {
        ...parentPlace,
        inhabitantIds: appendUniqueId(parentPlace.inhabitantIds, beingId),
      },
    };
  }

  let updatedGraph = nextGraph;
  const ownedItems = Array.isArray(being?.ownedItems) ? being.ownedItems : [];
  ownedItems.forEach((item) => {
    updatedGraph = mergeItem(updatedGraph, item, { ownerType: 'being', ownerId: beingId });
  });

  return updatedGraph;
};

const buildGraphFromPlace = async (placeId, currentGraph, options = {}) => {
  const place = await callCdo(cdoPlace.select, placeId);
  let nextGraph = mergePlace(currentGraph, place, options);

  const containedItems = Array.isArray(place?.containedItems) ? place.containedItems : [];
  containedItems.forEach((item) => {
    nextGraph = mergeItem(nextGraph, item, { ownerType: 'place', ownerId: placeId });
  });

  const savedInhabitants = Array.isArray(place?.savedInhabitants) ? place.savedInhabitants : [];
  const detailedBeings = await Promise.all(
    savedInhabitants.map(async (being) => {
      try {
        return await callCdo(cdoBeing.select, being.idEntity);
      } catch {
        return being;
      }
    })
  );

  detailedBeings.forEach((being) => {
    nextGraph = mergeBeing(nextGraph, being, { placeId });
  });

  const childPlaces = Array.isArray(place?.composedOfChildren) ? place.composedOfChildren : [];
  for (const childPlace of childPlaces) {
    if (!childPlace?.idEntity) {
      continue;
    }

    nextGraph = await buildGraphFromPlace(childPlace.idEntity, nextGraph, { parentPlaceId: placeId });
  }

  return nextGraph;
};

const buildPlaceSceneNode = (placeId, graph) => {
  const place = graph.placesById[placeId];
  if (!place) {
    return null;
  }

  return {
    idEntity: place.idEntity,
    name: getPlaceLabel(place),
    title: place.title || '',
    description: trimText(place.description || '', 220),
    level: place.level ?? '',
    placeType: place.placeType || '',
    zoneType: place.zoneType || '',
    buildingType: place.buildingType || '',
    transitPointType: place.transitPointType || '',
    location: place.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 },
    shape: place.shape ?? {},
    inhabitantCount: Array.isArray(place.inhabitantIds) ? place.inhabitantIds.length : 0,
    itemCount: Array.isArray(place.itemIds) ? place.itemIds.length : 0,
    children: (Array.isArray(place.childPlaceIds) ? place.childPlaceIds : [])
      .map((childPlaceId) => buildPlaceSceneNode(childPlaceId, graph))
      .filter(Boolean),
  };
};

const calculatePlaceBounds = (place) => {
  const shape = place?.shape ?? {};
  const location = place?.location ?? {};
  const radius = Number(location.maxRadius) || 0;

  const minX = Number(shape.minX);
  const maxX = Number(shape.maxX);
  const minZ = Number(shape.minZ);
  const maxZ = Number(shape.maxZ);

  if ([minX, maxX, minZ, maxZ].every((value) => Number.isFinite(value))) {
    return { minX, maxX, minZ, maxZ };
  }

  const centerX = Number(location.x) || 0;
  const centerZ = Number(location.z) || 0;
  return {
    minX: centerX - radius,
    maxX: centerX + radius,
    minZ: centerZ - radius,
    maxZ: centerZ + radius,
  };
};

const buildSettlementScene = (graph) => {
  if (!graph?.rootPlaceId || !graph.placesById[graph.rootPlaceId]) {
    return null;
  }

  const places = Object.values(graph.placesById);
  const rootPlace = graph.placesById[graph.rootPlaceId];
  const worldBounds = places.reduce(
    (bounds, place) => {
      const placeBounds = calculatePlaceBounds(place);
      return {
        minX: Math.min(bounds.minX, placeBounds.minX),
        maxX: Math.max(bounds.maxX, placeBounds.maxX),
        minZ: Math.min(bounds.minZ, placeBounds.minZ),
        maxZ: Math.max(bounds.maxZ, placeBounds.maxZ),
      };
    },
    { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity }
  );

  return {
    rootPlaceId: rootPlace.idEntity,
    settlement: {
      idEntity: rootPlace.idEntity,
      name: getPlaceLabel(rootPlace),
      title: rootPlace.title || '',
      description: trimText(rootPlace.description || '', 420),
      level: rootPlace.level ?? '',
      placeType: rootPlace.placeType || '',
      zoneType: rootPlace.zoneType || '',
      buildingType: rootPlace.buildingType || '',
      transitPointType: rootPlace.transitPointType || '',
      location: rootPlace.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 },
      shape: rootPlace.shape ?? {},
    },
    counts: {
      places: Object.keys(graph.placesById).length,
      inhabitants: Object.keys(graph.beingsById).length,
      items: Object.keys(graph.itemsById).length,
    },
    worldBounds: Number.isFinite(worldBounds.minX)
      ? worldBounds
      : { minX: 0, maxX: 0, minZ: 0, maxZ: 0 },
    hierarchy: buildPlaceSceneNode(graph.rootPlaceId, graph),
    places: places.map((place) => ({
      idEntity: place.idEntity,
      name: getPlaceLabel(place),
      title: place.title || '',
      description: trimText(place.description || '', 180),
      level: place.level ?? '',
      placeType: place.placeType || '',
      zoneType: place.zoneType || '',
      buildingType: place.buildingType || '',
      transitPointType: place.transitPointType || '',
      location: place.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 },
      shape: place.shape ?? {},
      childPlaceIds: Array.isArray(place.childPlaceIds) ? place.childPlaceIds : [],
      containedItems: Array.isArray(place.itemIds)
        ? place.itemIds.map((itemId) => graph.itemsById[itemId]).filter(Boolean)
        : [],
      currencies: Array.isArray(place.currencies) ? place.currencies : [],
      inhabitantCount: Array.isArray(place.inhabitantIds) ? place.inhabitantIds.length : 0,
      itemCount: Array.isArray(place.itemIds) ? place.itemIds.length : 0,
    })),
  };
};

const buildSettlementInteractionInhabitants = (graph) =>
  Object.values(graph.beingsById)
    .map((being) => {
      const place = being?.placeId ? graph.placesById[being.placeId] : null;
      const items = Array.isArray(being?.itemIds)
        ? being.itemIds.map((itemId) => graph.itemsById[itemId]).filter(Boolean)
        : [];
      const placeStockItems = Array.isArray(place?.itemIds)
        ? place.itemIds.map((itemId) => graph.itemsById[itemId]).filter(Boolean)
        : [];

      return {
        idEntity: being?.idEntity ?? '',
        name: getBeingLabel(being),
        title: being?.title ?? '',
        description: trimText(being?.description ?? '', 220),
        level: being?.level ?? '',
        beingType: being?.beingType ?? '',
        alignment: being?.alignment ?? '',
        personality: trimText(being?.personality ?? '', 160),
        objectivesShortTerm: trimText(being?.objectivesShortTerm ?? '', 220),
        objectivesLongTerm: trimText(being?.objectivesLongTerm ?? '', 220),
        strength: being?.strength ?? 0,
        agility: being?.agility ?? 0,
        constitution: being?.constitution ?? 0,
        intellect: being?.intellect ?? 0,
        wisdom: being?.wisdom ?? 0,
        charisma: being?.charisma ?? 0,
        location: being?.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 },
        memberPlaceId: place?.idEntity ?? '',
        memberPlaceName: place ? getPlaceLabel(place) : '',
        items: items.map((item) => ({
          ...item,
          value: Number(item?.value) || 0,
        })),
        placeStockItems: placeStockItems.map((item) => ({
          ...item,
          value: Number(item?.value) || 0,
        })),
        currencies: Array.isArray(being?.currencies) ? being.currencies : [],
        spells: Array.isArray(being?.spells) ? being.spells : [],
        relationships: Array.isArray(being?.relationships)
          ? being.relationships.map((relationship) => ({
              relateType: relationship?.relateType ?? '',
              direction: relationship?.direction ?? '',
              otherEntityId: relationship?.otherEntityId ?? '',
              otherEntityName: relationship?.otherEntityName ?? '',
              otherEntityTitle: relationship?.otherEntityTitle ?? '',
              otherEntityType: relationship?.otherEntityType ?? '',
              score: Number.isFinite(Number(relationship?.score)) ? Number(relationship.score) : 50,
              demeanor: relationship?.demeanor ?? '',
            }))
          : [],
      };
    })
    .filter((being) => being.idEntity);

const getShapeBounds = (place) => {
  const shape = place?.shape ?? {};
  const location = place?.location ?? {};
  const radius = Math.max(10, Number(location.maxRadius) || 10);
  const centerX = Number(location.x) || 0;
  const centerZ = Number(location.z) || 0;

  const minX = Number(shape.minX);
  const maxX = Number(shape.maxX);
  const minZ = Number(shape.minZ);
  const maxZ = Number(shape.maxZ);

  if ([minX, maxX, minZ, maxZ].every((value) => Number.isFinite(value))) {
    return { minX, maxX, minZ, maxZ };
  }

  return {
    minX: centerX - radius,
    maxX: centerX + radius,
    minZ: centerZ - radius,
    maxZ: centerZ + radius,
  };
};

const buildTopDownPlan = (graph, interactionState = {}) => {
  if (!graph?.rootPlaceId || !graph.placesById[graph.rootPlaceId]) {
    return null;
  }

  const places = Object.values(graph.placesById);
  if (places.length === 0) {
    return null;
  }

  const overallBounds = places.reduce(
    (bounds, place) => {
      const placeBounds = getShapeBounds(place);
      return {
        minX: Math.min(bounds.minX, placeBounds.minX),
        maxX: Math.max(bounds.maxX, placeBounds.maxX),
        minZ: Math.min(bounds.minZ, placeBounds.minZ),
        maxZ: Math.max(bounds.maxZ, placeBounds.maxZ),
      };
    },
    { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity }
  );

  const width = Math.max(1, overallBounds.maxX - overallBounds.minX);
  const height = Math.max(1, overallBounds.maxZ - overallBounds.minZ);
  const padding = 24;
  const drawableSize = PLAN_VIEW_SIZE - padding * 2;
  const scale = Math.min(drawableSize / width, drawableSize / height);
  const offsetX = padding + (drawableSize - width * scale) / 2;
  const offsetY = padding + (drawableSize - height * scale) / 2;

  const toCanvasX = (x) => offsetX + (x - overallBounds.minX) * scale;
  const toCanvasY = (z) => offsetY + (z - overallBounds.minZ) * scale;

  const rootPlaceId = graph.rootPlaceId;
  const legendMap = new Map();

  const nodes = places
    .map((place) => {
      const bounds = getShapeBounds(place);
      const placeType = String(place?.placeType ?? '').toLowerCase();
      const buildingType = String(place?.buildingType ?? '').toLowerCase();
      const isSettlementRoot = place.idEntity === rootPlaceId;
      const isFortification =
        placeType.includes('fortification') ||
        buildingType.includes('gatehouse') ||
        buildingType.includes('watchtower') ||
        buildingType.includes('barracks') ||
        buildingType.includes('armory');
      const categoryLabel = resolvePlanCategory(place);
      const typeColors = getPlanCategoryColor(categoryLabel);

      if (!isSettlementRoot) {
        const key = normalizePlanCategoryKey(categoryLabel);
        if (!legendMap.has(key)) {
          legendMap.set(key, {
            key,
            label: categoryLabel,
            fill: typeColors.fill,
            stroke: typeColors.stroke,
          });
        }
      }

      return {
        id: place.idEntity,
        name: getPlaceLabel(place),
        title: place.title || '',
        x: toCanvasX(bounds.minX),
        y: toCanvasY(bounds.minZ),
        width: Math.max(4, (bounds.maxX - bounds.minX) * scale),
        height: Math.max(4, (bounds.maxZ - bounds.minZ) * scale),
        isSettlementRoot,
        isFortification,
        planCategory: categoryLabel,
        fill: isSettlementRoot ? 'none' : typeColors.fill,
        stroke: isSettlementRoot ? '#1f4e79' : isFortification ? '#7a4f17' : typeColors.stroke,
      };
    })
    .sort((left, right) => {
      if (left.isSettlementRoot !== right.isSettlementRoot) {
        return left.isSettlementRoot ? -1 : 1;
      }
      return right.width * right.height - left.width * left.height;
    });

  const inhabitants = Object.values(graph.beingsById)
    .filter((being) => being?.location && Number.isFinite(Number(being.location.x)) && Number.isFinite(Number(being.location.z)))
    .map((being) => {
      const centerX = Number(being.location.x) || 0;
      const centerZ = Number(being.location.z) || 0;

      return {
        id: being.idEntity,
        name: getBeingLabel(being),
        x: toCanvasX(centerX),
        y: toCanvasY(centerZ),
        radius: 2.75,
      };
    });

  const playerPath = Array.isArray(interactionState?.playerPath)
    ? interactionState.playerPath
        .map((point) => ({
          x: toCanvasX(Number(point?.x) || 0),
          y: toCanvasY(Number(point?.z) || 0),
        }))
        .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
    : [];
  const playerLocation =
    interactionState?.playerLocation &&
    Number.isFinite(Number(interactionState.playerLocation.x)) &&
    Number.isFinite(Number(interactionState.playerLocation.z))
      ? {
          x: toCanvasX(Number(interactionState.playerLocation.x) || 0),
          y: toCanvasY(Number(interactionState.playerLocation.z) || 0),
        }
      : null;

  return {
    width: PLAN_VIEW_SIZE,
    height: PLAN_VIEW_SIZE,
    nodes,
    inhabitants,
    playerPath,
    playerLocation,
    legend: [...legendMap.values()].sort((left, right) => left.label.localeCompare(right.label)),
  };
};

function Explore() {
  const [settlements, setSettlements] = useState([]);
  const [allBeings, setAllBeings] = useState([]);
  const [selectedSettlementId, setSelectedSettlementId] = useState('');
  const [selectedExplorerBeingId, setSelectedExplorerBeingId] = useState('');
  const [graph, setGraph] = useState(createEmptyGraph());
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingSettlements, setIsLoadingSettlements] = useState(false);
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [isDeletingEvents, setIsDeletingEvents] = useState(false);
  const [selectedNodeKey, setSelectedNodeKey] = useState('');
  const [approachImage, setApproachImage] = useState(createEmptyImageState());
  const [interactionState, setInteractionState] = useState(createEmptyInteractionState());
  const [topDownZoom, setTopDownZoom] = useState(1);
  const latestSettlementIdRef = useRef('');
  const chatLogRef = useRef(null);

  const commitGraph = (nextGraph) => {
    setGraph(nextGraph);
    return nextGraph;
  };

  const applyInhabitantLocationsToGraph = useCallback((placements) => {
    setGraph((currentGraph) => {
      const nextBeingsById = { ...currentGraph.beingsById };

      (Array.isArray(placements) ? placements : []).forEach((placement) => {
        const beingId = String(placement?.beingId ?? '').trim();
        if (!beingId || !nextBeingsById[beingId]) {
          return;
        }

        nextBeingsById[beingId] = {
          ...nextBeingsById[beingId],
          location: {
            x: Number(placement?.x) || 0,
            y: Number(placement?.y) || 0,
            z: Number(placement?.z) || 0,
            maxRadius: Math.max(0.5, Number(placement?.maxRadius) || 3),
          },
        };
      });

      return {
        ...currentGraph,
        beingsById: nextBeingsById,
      };
    });
  }, []);

  const applyTradeResultToGraph = useCallback((tradeResult) => {
    if (!tradeResult) {
      return;
    }

    setGraph((currentGraph) => {
      let nextGraph = currentGraph;
      const itemId = String(tradeResult?.itemId ?? '').trim();
      const previousOwnerId = String(tradeResult?.previousOwnerId ?? '').trim();
      const previousOwnerType = String(tradeResult?.previousOwnerType ?? 'being').trim();
      const nextOwnerId = String(tradeResult?.nextOwnerId ?? '').trim();
      const nextOwnerType = String(tradeResult?.nextOwnerType ?? 'being').trim();

      if (tradeResult.executed && itemId && nextOwnerId) {
        const currentItem = nextGraph.itemsById[itemId] ?? {
          idEntity: itemId,
          name: tradeResult.itemName || itemId,
          title: '',
          description: '',
          level: '',
          itemType: '',
          qualityLevel: 0,
          magicLevel: 0,
          value: Number(tradeResult.itemValueGp) || 0,
        };

        const nextBeingsById = { ...nextGraph.beingsById };
        if (previousOwnerId && previousOwnerType !== 'place' && nextBeingsById[previousOwnerId]) {
          nextBeingsById[previousOwnerId] = {
            ...nextBeingsById[previousOwnerId],
            itemIds: (nextBeingsById[previousOwnerId].itemIds ?? []).filter((ownedItemId) => ownedItemId !== itemId),
          };
        }
        if (nextOwnerId && nextOwnerType !== 'place' && nextBeingsById[nextOwnerId]) {
          nextBeingsById[nextOwnerId] = {
            ...nextBeingsById[nextOwnerId],
            itemIds: appendUniqueId(nextBeingsById[nextOwnerId].itemIds, itemId),
          };
        }

        const nextPlacesById = { ...nextGraph.placesById };
        if (previousOwnerId && previousOwnerType === 'place' && nextPlacesById[previousOwnerId]) {
          nextPlacesById[previousOwnerId] = {
            ...nextPlacesById[previousOwnerId],
            itemIds: (nextPlacesById[previousOwnerId].itemIds ?? []).filter((containedItemId) => containedItemId !== itemId),
          };
        }
        if (nextOwnerId && nextOwnerType === 'place' && nextPlacesById[nextOwnerId]) {
          nextPlacesById[nextOwnerId] = {
            ...nextPlacesById[nextOwnerId],
            itemIds: appendUniqueId(nextPlacesById[nextOwnerId].itemIds, itemId),
          };
        }

        nextGraph = {
          ...nextGraph,
          beingsById: nextBeingsById,
          placesById: nextPlacesById,
          itemsById: {
            ...nextGraph.itemsById,
            [itemId]: {
              ...currentItem,
              ownerType: nextOwnerType === 'place' ? 'place' : 'being',
              ownerId: nextOwnerId,
              value: Number(tradeResult.itemValueGp) || Number(currentItem.value) || 0,
            },
          },
        };
      }

      const applyCurrencies = (graph, idEntity, currencies) => {
        if (!idEntity || !Array.isArray(currencies)) {
          return graph;
        }
        if (graph.beingsById[idEntity]) {
          return {
            ...graph,
            beingsById: {
              ...graph.beingsById,
              [idEntity]: {
                ...graph.beingsById[idEntity],
                currencies,
              },
            },
          };
        }
        if (graph.placesById[idEntity]) {
          return {
            ...graph,
            placesById: {
              ...graph.placesById,
              [idEntity]: {
                ...graph.placesById[idEntity],
                currencies,
              },
            },
          };
        }
        return {
          ...graph
        }
      };

      nextGraph = applyCurrencies(nextGraph, tradeResult.buyerId, tradeResult.buyerCurrencies);
      nextGraph = applyCurrencies(nextGraph, tradeResult.sellerId, tradeResult.sellerCurrencies);
      return nextGraph;
    });
  }, []);

  const settlementInhabitants = useMemo(() => buildSettlementInteractionInhabitants(graph), [graph]);
  const explorerOptions = useMemo(() => {
    const inhabitantIds = new Set(Object.keys(graph.beingsById));

    return (Array.isArray(allBeings) ? allBeings : [])
      .filter((being) => being?.idEntity && !inhabitantIds.has(being.idEntity))
      .map((being) => {
        const name = getBeingLabel(being);
        const title = being?.entityTitle ?? being?.title ?? '';

        return {
          value: being.idEntity,
          label: `${name}${title ? ` - ${title}` : ''}`,
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [allBeings, graph.beingsById]);

  const loadSettlements = useCallback(async () => {
    setIsLoadingSettlements(true);
    setErrorMessage('');

    try {
      const places = await callCdo(cdoPlace.selectAll);
      const settlementOptions = (Array.isArray(places) ? places : [])
        .filter((place) => String(place?.placeType ?? '').trim() === 'Settlement')
        .map((place) => ({
          idEntity: place.idEntity,
          name: getPlaceLabel(place),
          title: place?.entityTitle ?? place?.title ?? '',
        }))
        .sort((left, right) => left.name.localeCompare(right.name));

      setSettlements(settlementOptions);
      if (settlementOptions.length === 0) {
        setStatusMessage('No saved settlements found.');
        setSelectedSettlementId('');
        setGraph(createEmptyGraph());
        setSelectedNodeKey('');
        setApproachImage(createEmptyImageState());
        setTopDownZoom(1);
        return;
      }

      setStatusMessage('');
      setSelectedSettlementId((current) =>
        current && settlementOptions.some((option) => option.idEntity === current) ? current : settlementOptions[0].idEntity
      );
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to load settlements.');
      setSettlements([]);
    } finally {
      setIsLoadingSettlements(false);
    }
  }, []);

  const loadAllBeings = useCallback(async () => {
    try {
      const beings = await callCdo(cdoBeing.selectAll);
      setAllBeings(Array.isArray(beings) ? beings : []);
    } catch (error) {
      setAllBeings([]);
      setErrorMessage((current) => current || error?.message || 'Failed to load beings.');
    }
  }, []);

  useEffect(() => {
    latestSettlementIdRef.current = selectedSettlementId;
  }, [selectedSettlementId]);

  const loadSavedApproachImage = useCallback(async (settlementId) => {
    if (!settlementId) {
      setApproachImage(createEmptyImageState());
      return;
    }

    setApproachImage({ ...createEmptyImageState(), isLoadingSavedImage: true });
    try {
      const response = await callCdo(cdoPlace.selectApproachImage.bind(cdoPlace), settlementId);

      if (latestSettlementIdRef.current !== settlementId) {
        return;
      }

      if (!mediaSource(response)) {
        setApproachImage({ ...createEmptyImageState(), hasCheckedSavedImage: true });
        return;
      }

      setApproachImage({
        src: mediaSource(response),
        revisedPrompt: response?.revisedPrompt || '',
        isLoading: false,
        isLoadingSavedImage: false,
        isSaving: false,
        isSaved: true,
        hasCheckedSavedImage: true,
        error: '',
      });
    } catch (error) {
      if (latestSettlementIdRef.current !== settlementId) {
        return;
      }

      setApproachImage((current) => ({
        ...current,
        isLoading: false,
        isLoadingSavedImage: false,
        isSaving: false,
        error: current.src ? current.error : error?.message || 'Failed to load saved approach image.',
      }));
    }
  }, []);

  const loadSettlementTree = useCallback(async (settlementId) => {
    if (!settlementId) {
      setGraph(createEmptyGraph());
      setSelectedNodeKey('');
      setStatusMessage('');
      setApproachImage(createEmptyImageState());
      setInteractionState(createEmptyInteractionState());
      setSelectedExplorerBeingId('');
      setTopDownZoom(1);
      return;
    }

    setIsLoadingTree(true);
    setErrorMessage('');
    setStatusMessage('Loading settlement...');
    setApproachImage(createEmptyImageState());
    setInteractionState(createEmptyInteractionState());
    setTopDownZoom(1);

    try {
      const nextGraph = await buildGraphFromPlace(settlementId, createEmptyGraph(), { setAsRoot: true });
      commitGraph(nextGraph);
      setSelectedNodeKey(`place:${settlementId}`);

      const counts = {
        places: Object.keys(nextGraph.placesById).length,
        inhabitants: Object.keys(nextGraph.beingsById).length,
        items: Object.keys(nextGraph.itemsById).length,
      };
      setStatusMessage(
        `Loaded settlement tree with ${counts.places} place(s), ${counts.inhabitants} inhabitant(s), and ${counts.items} item(s).`
      );
      loadSavedApproachImage(settlementId);
    } catch (error) {
      setGraph(createEmptyGraph());
      setSelectedNodeKey('');
      setErrorMessage(error?.message || 'Failed to load settlement tree.');
      setStatusMessage('');
    } finally {
      setIsLoadingTree(false);
    }
  }, [loadSavedApproachImage]);

  useEffect(() => {
    loadSettlements();
    loadAllBeings();
  }, [loadAllBeings, loadSettlements]);

  useEffect(() => {
    if (selectedSettlementId) {
      loadSettlementTree(selectedSettlementId);
    }
  }, [loadSettlementTree, selectedSettlementId]);

  useEffect(() => {
    if (explorerOptions.length === 0) {
      setSelectedExplorerBeingId('');
      return;
    }

    setSelectedExplorerBeingId((current) =>
      current && explorerOptions.some((option) => option.value === current) ? current : explorerOptions[0].value
    );
  }, [explorerOptions]);

  useEffect(() => {
    setInteractionState(createEmptyInteractionState());
  }, [selectedExplorerBeingId]);

  useEffect(() => {
    const chatLog = chatLogRef.current;
    if (!chatLog) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      chatLog.scrollTop = chatLog.scrollHeight;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [interactionState.messages.length]);

  const settlementScene = useMemo(() => buildSettlementScene(graph), [graph]);
  const topDownPlan = useMemo(() => buildTopDownPlan(graph, interactionState), [graph, interactionState]);

  const applyInteractionResponseToGraph = useCallback((response) => {
    applyInhabitantLocationsToGraph(response?.inhabitantLocations);
    applyTradeResultToGraph(response?.tradeResult);
  }, [applyInhabitantLocationsToGraph, applyTradeResultToGraph]);

  const startSettlementInteraction = useCallback(async () => {
    if (!settlementScene || !selectedExplorerBeingId) {
      return;
    }

    setInteractionState((current) => ({
      ...current,
      isLoading: true,
      error: '',
    }));

    try {
      const explorer = await callCdo(cdoBeing.select.bind(cdoBeing), selectedExplorerBeingId);
      const response = await callCdo(cdoAI.startSettlementInteraction.bind(cdoAI), {
        settlementScene,
        inhabitants: settlementInhabitants,
        explorer,
      });

      applyInteractionResponseToGraph(response);

      setInteractionState({
        sessionKey: response?.sessionKey || '',
        started: true,
        messages: response?.description
          ? [{ role: 'assistant', content: response.description }]
          : [],
        draft: '',
        isLoading: false,
        error: '',
        playerLocation: response?.currentLocation ?? null,
        playerPath: Array.isArray(response?.path) ? response.path : [],
        visibilitySnapshot: response?.visibilitySnapshot ?? null,
        actionComplete: response?.actionComplete !== false,
        pendingActionSummary: response?.pendingActionSummary || '',
      });

      setStatusMessage('Settlement interaction started.');
    } catch (error) {
      setInteractionState((current) => ({
        ...current,
        isLoading: false,
        error: error?.message || 'Failed to start settlement interaction.',
      }));
    }
  }, [applyInteractionResponseToGraph, selectedExplorerBeingId, settlementInhabitants, settlementScene]);

  const submitSettlementInteraction = useCallback(
    async ({ instruction = '', interactionMode = '', continueAction = false, interruptAction = false } = {}) => {
      if (!interactionState.sessionKey || !instruction.trim()) {
        return;
      }

      const nextInstruction = instruction.trim();
      setInteractionState((current) => ({
        ...current,
        isLoading: true,
        error: '',
      }));

      try {
        const response = await callCdo(cdoAI.interactWithSettlement.bind(cdoAI), {
          sessionKey: interactionState.sessionKey,
          instruction: nextInstruction,
          interactionMode,
          continueAction,
          interruptAction,
        });

        applyInteractionResponseToGraph(response);

        setInteractionState((current) => ({
          ...current,
          isLoading: false,
          error: '',
          playerLocation: response?.currentLocation ?? current.playerLocation,
          playerPath: Array.isArray(response?.path) ? response.path : [],
          visibilitySnapshot: response?.visibilitySnapshot ?? current.visibilitySnapshot,
          actionComplete: response?.actionComplete !== false,
          pendingActionSummary: response?.pendingActionSummary || '',
          draft: '',
          messages: [
            ...current.messages,
            {
              role: 'user',
              content: nextInstruction,
            },
            {
              role: 'assistant',
              content: response?.description || 'No description returned.',
            },
          ],
        }));
      } catch (error) {
        setInteractionState((current) => ({
          ...current,
          isLoading: false,
          error: error?.message || 'Failed to interact with settlement.',
        }));
      }
    },
    [applyInteractionResponseToGraph, interactionState.sessionKey]
  );

  const submitDraftInteraction = useCallback(() => {
    const instruction = interactionState.draft.trim();
    if (!interactionState.started || interactionState.isLoading || !instruction) {
      return;
    }

    submitSettlementInteraction({ instruction });
  }, [interactionState.draft, interactionState.isLoading, interactionState.started, submitSettlementInteraction]);

  const deleteAllEvents = useCallback(async () => {
    if (isDeletingEvents) {
      return;
    }

    const confirmed = window.confirm('Delete all Events and Event memory records? This cannot be undone.');
    if (!confirmed) {
      return;
    }

    setIsDeletingEvents(true);
    setErrorMessage('');
    setStatusMessage('Deleting Events...');

    try {
      const response = await callCdo(cdoEvent.deleteAll.bind(cdoEvent));
      const deletedEvents = Number(response?.deletedEvents ?? response?.eventCount) || 0;
      const deletedRelationships = Number(response?.deletedEventRelationships) || 0;
      setInteractionState(createEmptyInteractionState());
      setStatusMessage(
        `Deleted ${deletedEvents} event(s) and ${deletedRelationships} event relationship(s). Settlement session reset.`
      );
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to delete Events.');
      setStatusMessage('');
    } finally {
      setIsDeletingEvents(false);
    }
  }, [isDeletingEvents]);

  const persistApproachImage = useCallback(async (image) => {
    if (!selectedSettlementId || !image?.src) {
      return false;
    }

    const base64Marker = ';base64,';
    const markerIndex = image.src.indexOf(base64Marker);
    if (markerIndex < 0) {
      setApproachImage((current) => ({
        ...current,
        error: 'Generated image data is not in a savable format.',
      }));
      return false;
    }

    const mediaType = image.src.slice(5, markerIndex) || 'image/png';
    const imageBase64 = image.src.slice(markerIndex + base64Marker.length);
    const settlement = graph.placesById[selectedSettlementId];

    setApproachImage((current) => ({
      ...current,
      isSaving: true,
      error: '',
    }));

    try {
      await callCdo(cdoPlace.saveApproachImage.bind(cdoPlace), {
        idEntity: selectedSettlementId,
        name: settlement?.name || settlement?.entityName || '',
        title: settlement?.title || settlement?.entityTitle || '',
        mediaType,
        imageBase64,
        revisedPrompt: image.revisedPrompt || '',
      });

      setApproachImage((current) => ({
        ...current,
        isSaving: false,
        isSaved: true,
        error: '',
      }));
      return true;
    } catch (error) {
      setApproachImage((current) => ({
        ...current,
        isSaving: false,
        isSaved: false,
        error: error?.message || 'Failed to save approach image.',
      }));
      return false;
    }
  }, [graph.placesById, selectedSettlementId]);

  const generateApproachImage = useCallback(async () => {
    if (!settlementScene) {
      return;
    }

    const shouldAutoSaveGeneratedImage = approachImage.hasCheckedSavedImage && !approachImage.src;
    const setImageState = setApproachImage;
    const generateFn = cdoAI.generateSettlementApproachImage;

    setImageState((current) => ({
      ...current,
      isLoading: true,
      error: '',
    }));

    try {
      const response = await callCdo(generateFn.bind(cdoAI), { settlementScene });
      if (!response?.imageBase64) {
        throw new Error('Image generation returned no image data.');
      }
      const generatedImage = {
        src: `data:${response?.mimeType || 'image/png'};base64,${response.imageBase64}`,
        revisedPrompt: response?.revisedPrompt || '',
        isLoading: false,
        isLoadingSavedImage: false,
        isSaving: false,
        isSaved: false,
        hasCheckedSavedImage: true,
        error: '',
      };
      setImageState(generatedImage);
      if (shouldAutoSaveGeneratedImage && generatedImage.src) {
        await persistApproachImage(generatedImage);
      }
    } catch (error) {
      setImageState((current) => ({
        ...current,
        isLoading: false,
        isSaving: false,
        error: error?.message || 'Failed to generate image.',
      }));
    }
  }, [approachImage.hasCheckedSavedImage, approachImage.src, persistApproachImage, settlementScene]);

  const saveApproachImage = useCallback(async () => {
    await persistApproachImage(approachImage);
  }, [approachImage, persistApproachImage]);

  const selectedNode = useMemo(() => {
    if (!selectedNodeKey) {
      return null;
    }

    const [nodeType, nodeId] = selectedNodeKey.split(':');
    if (!nodeType || !nodeId) {
      return null;
    }

    if (nodeType === 'place') {
      const place = graph.placesById[nodeId];
      return place ? { type: 'place', value: place } : null;
    }

    if (nodeType === 'being') {
      const being = graph.beingsById[nodeId];
      return being ? { type: 'being', value: being } : null;
    }

    if (nodeType === 'item') {
      const item = graph.itemsById[nodeId];
      return item ? { type: 'item', value: item } : null;
    }

    return null;
  }, [graph, selectedNodeKey]);

  return (
    <div className="explore-panel">
      <section className="editor-section">
        <div className="explore-selector-grid">
          <label className="editor-field" htmlFor="explore-settlement-select">
            Settlement
            <select
              id="explore-settlement-select"
              className="select-input"
              value={selectedSettlementId}
              onChange={(event) => setSelectedSettlementId(event.target.value)}
              disabled={isLoadingSettlements || isLoadingTree || settlements.length === 0}
            >
              {settlements.length === 0 && <option value="">No settlements available</option>}
              {settlements.map((settlement) => (
                <option key={settlement.idEntity} value={settlement.idEntity}>
                  {settlement.name}
                </option>
              ))}
            </select>
          </label>

          <label className="editor-field" htmlFor="explore-being-select">
            Explorer
            <select
              id="explore-being-select"
              className="select-input"
              value={selectedExplorerBeingId}
              onChange={(event) => setSelectedExplorerBeingId(event.target.value)}
              disabled={isLoadingTree || explorerOptions.length === 0}
            >
              {explorerOptions.length === 0 && <option value="">No external beings available</option>}
              {explorerOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className="explore-selector-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={startSettlementInteraction}
              disabled={!settlementScene || !selectedExplorerBeingId || isLoadingTree || interactionState.isLoading || isDeletingEvents}
            >
              {interactionState.isLoading && !interactionState.started ? 'Starting...' : 'Start'}
            </button>

            <button
              type="button"
              className="secondary-button"
              onClick={deleteAllEvents}
              disabled={isDeletingEvents || interactionState.isLoading}
            >
              {isDeletingEvents ? 'Deleting Events...' : 'Delete Events'}
            </button>
          </div>
        </div>
      </section>

      {statusMessage && <div className="panel-status">{statusMessage}</div>}
      {errorMessage && <div className="panel-status error-status">{errorMessage}</div>}

      {graph.rootPlaceId && (
        <section className="editor-section">
          <div className="explore-main-grid">
            <div className="explore-settlement-views-panel">
              <h3>Settlement Views</h3>
              <div className="explore-image-card">
                <h4>Approach View</h4>
                <div className="explore-image-frame">
                  {approachImage.src ? (
                    <MediaImage className="explore-image" src={approachImage.src} alt="Generated approach view of the settlement." />
                  ) : (
                    <div className="explore-image-placeholder">
                      {approachImage.isLoadingSavedImage
                        ? 'Loading saved approach image...'
                        : approachImage.isLoading ? 'Generating approach image...' : 'No approach image generated yet.'}
                    </div>
                  )}
                </div>
                {approachImage.error && <div className="form-error">{approachImage.error}</div>}
                <div className="button-row">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={generateApproachImage}
                    disabled={isLoadingTree || approachImage.isLoadingSavedImage || approachImage.isLoading || !settlementScene}
                  >
                    {approachImage.isLoading ? 'Generating...' : 'Generate Image'}
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={saveApproachImage}
                    disabled={
                      isLoadingTree ||
                      approachImage.isLoadingSavedImage ||
                      approachImage.isLoading ||
                      approachImage.isSaving ||
                      !approachImage.src ||
                      !selectedSettlementId
                    }
                  >
                    {approachImage.isSaving ? 'Saving...' : approachImage.isSaved ? 'Saved' : 'Save Image'}
                  </button>
                </div>
              </div>

              <div className="explore-image-card">
                <h4>Top-Down View</h4>
                <div className="button-row">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() =>
                      setTopDownZoom((current) => Math.max(PLAN_VIEW_MIN_ZOOM, current - PLAN_VIEW_ZOOM_STEP))
                    }
                    disabled={!topDownPlan || topDownZoom <= PLAN_VIEW_MIN_ZOOM}
                  >
                    Zoom Out
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() =>
                      setTopDownZoom((current) => Math.min(PLAN_VIEW_MAX_ZOOM, current + PLAN_VIEW_ZOOM_STEP))
                    }
                    disabled={!topDownPlan || topDownZoom >= PLAN_VIEW_MAX_ZOOM}
                  >
                    Zoom In
                  </button>
                  <span className="existing-list-title">Zoom {topDownZoom.toFixed(2)}x</span>
                </div>
                <div className="explore-image-frame">
                  {topDownPlan ? (
                    <svg
                      className="explore-plan-svg"
                      viewBox={`0 0 ${topDownPlan.width} ${topDownPlan.height}`}
                      role="img"
                      aria-label="Top-down line view of the settlement based on saved place coordinates."
                    >
                      <rect x="0" y="0" width={topDownPlan.width} height={topDownPlan.height} fill="#f8fbff" />
                      <g
                        transform={`translate(${topDownPlan.width / 2} ${topDownPlan.height / 2}) scale(${topDownZoom}) translate(${-topDownPlan.width / 2} ${-topDownPlan.height / 2})`}
                      >
                        {topDownPlan.nodes.map((node) => (
                          <g key={node.id}>
                            <rect
                              x={node.x}
                              y={node.y}
                              width={node.width}
                              height={node.height}
                              fill={node.fill}
                              stroke={node.stroke}
                              strokeWidth={node.isSettlementRoot ? 2.5 : node.isFortification ? 2 : 1.5}
                              className={`explore-plan-shape ${
                                selectedNodeKey === `place:${node.id}` ? 'explore-plan-shape-active' : ''
                              }`}
                              onClick={() => setSelectedNodeKey(`place:${node.id}`)}
                            />
                          </g>
                        ))}
                        {topDownPlan.inhabitants.map((inhabitant) => (
                          <circle
                            key={inhabitant.id}
                            className="explore-plan-inhabitant"
                            cx={inhabitant.x}
                            cy={inhabitant.y}
                            r={inhabitant.radius}
                          />
                        ))}
                        {topDownPlan.playerPath.length > 1 && (
                          <polyline
                            className="explore-plan-player-path"
                            points={topDownPlan.playerPath.map((point) => `${point.x},${point.y}`).join(' ')}
                          />
                        )}
                        {topDownPlan.playerLocation && (
                          <circle
                            className="explore-plan-player"
                            cx={topDownPlan.playerLocation.x}
                            cy={topDownPlan.playerLocation.y}
                            r="5"
                          />
                        )}
                      </g>
                    </svg>
                  ) : (
                    <div className="explore-image-placeholder">No top-down line view available yet.</div>
                  )}
                </div>
                <div className="existing-list-title">
                  {selectedNode?.type === 'place'
                    ? `Selected place: ${getPlaceLabel(selectedNode.value)}${
                        selectedNode.value.title ? ` - ${selectedNode.value.title}` : ''
                      }`
                    : 'Click a shape to display the place name.'}
                </div>
                {topDownPlan?.legend?.length > 0 && (
                  <div className="explore-plan-legend" aria-label="Top-down view legend">
                    {topDownPlan.legend.map((entry) => (
                      <div key={entry.key} className="explore-plan-legend-item">
                        <span
                          className="explore-plan-legend-swatch"
                          style={{ backgroundColor: entry.fill, borderColor: entry.stroke }}
                          aria-hidden="true"
                        />
                        <span>{entry.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="explore-interaction-panel">
              <h3>Interact with Settlement</h3>
              <div className="explore-chat-log" ref={chatLogRef}>
                {interactionState.messages.length === 0 ? (
                  <div className="explore-chat-empty">Press Start to initialize the settlement simulation.</div>
                ) : (
                  interactionState.messages.map((message, index) => (
                    <div
                      key={`${message.role}-${index}`}
                      className={`explore-chat-entry explore-chat-entry-${message.role}`}
                    >
                      {message.role === 'user' ? <strong>You</strong> : null}
                      <div className="explore-chat-content">{message.content}</div>
                    </div>
                  ))
                )}
              </div>
              {interactionState.error && <div className="form-error">{interactionState.error}</div>}
              {interactionState.started && interactionState.actionComplete === false && (
                <div className="explore-pending-action">
                  <div>
                    Current action continuing
                    {interactionState.pendingActionSummary ? `: ${interactionState.pendingActionSummary}` : '.'}
                  </div>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() =>
                      submitSettlementInteraction({
                        instruction: 'Continue current action.',
                        continueAction: true,
                      })
                    }
                    disabled={interactionState.isLoading}
                  >
                    Continue Action
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() =>
                      submitSettlementInteraction({
                        instruction: 'I stop what I am doing and reassess the situation.',
                        interruptAction: true,
                      })
                    }
                    disabled={interactionState.isLoading}
                  >
                    Interrupt Action
                  </button>
                </div>
              )}
              <label className="editor-field" htmlFor="explore-interaction-draft">
                <span className="field-label">Action or question</span>
                <textarea
                  id="explore-interaction-draft"
                  className="text-area explore-chat-textarea"
                  value={interactionState.draft}
                  onChange={(event) =>
                    setInteractionState((current) => ({
                      ...current,
                      draft: event.target.value,
                    }))
                  }
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent?.isComposing) {
                      return;
                    }

                    event.preventDefault();
                    submitDraftInteraction();
                  }}
                  disabled={!interactionState.started || interactionState.isLoading}
                />
              </label>
              <div className="button-row">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={submitDraftInteraction}
                  disabled={
                    !interactionState.started ||
                    interactionState.isLoading ||
                    !interactionState.draft.trim()
                  }
                >
                  {interactionState.isLoading ? 'Thinking...' : 'Submit'}
                </button>
              </div>
              {interactionState.playerLocation && (
                <div className="existing-list-title">
                  Current location: X {Number(interactionState.playerLocation.x).toFixed(1)}, Y{' '}
                  {Number(interactionState.playerLocation.y).toFixed(1)}, Z{' '}
                  {Number(interactionState.playerLocation.z).toFixed(1)}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {selectedNode && (
        <section className="editor-section">
          <h3>Details</h3>
          {selectedNode.type === 'place' && (
            <div className="explore-detail-grid">
              <div className="empty-grid">
                <strong>{getPlaceLabel(selectedNode.value)}</strong>
                <div>{selectedNode.value.title || 'No title'}</div>
              </div>
              <div className="empty-grid">
                <strong>Type</strong>
                <div>{[selectedNode.value.placeType, selectedNode.value.transitPointType, selectedNode.value.zoneType, selectedNode.value.buildingType].filter(Boolean).join(' / ') || 'Unknown'}</div>
              </div>
              <div className="empty-grid">
                <strong>Contents</strong>
                <div>
                  {(selectedNode.value.childPlaceIds ?? []).length} child place(s), {(selectedNode.value.inhabitantIds ?? []).length} inhabitant(s), {(selectedNode.value.itemIds ?? []).length} item(s)
                </div>
              </div>
              <div className="empty-grid explore-detail-span">
                <strong>Description</strong>
                <div>{selectedNode.value.description || 'No description.'}</div>
              </div>
            </div>
          )}

          {selectedNode.type === 'being' && (
            <div className="explore-detail-grid">
              <div className="empty-grid">
                <strong>{getBeingLabel(selectedNode.value)}</strong>
                <div>{selectedNode.value.title || 'No title'}</div>
              </div>
              <div className="empty-grid">
                <strong>Type</strong>
                <div>{[selectedNode.value.beingType, selectedNode.value.level !== '' ? `Lvl ${selectedNode.value.level}` : ''].filter(Boolean).join(' / ') || 'Unknown'}</div>
              </div>
              <div className="empty-grid">
                <strong>Inventory</strong>
                <div>{(selectedNode.value.itemIds ?? []).length} item(s)</div>
              </div>
              <div className="empty-grid">
                <strong>Currency</strong>
                <div>
                  {(selectedNode.value.currencies ?? [])
                    .filter((currency) => Number(currency?.quantity) > 0)
                    .map((currency) => `${currency.quantity} ${currency.currencyName || currency.idCurrency}`)
                    .join(', ') || 'None'}
                </div>
              </div>
              <div className="empty-grid explore-detail-span">
                <strong>Description</strong>
                <div>{selectedNode.value.description || selectedNode.value.personality || 'No description.'}</div>
              </div>
              <div className="empty-grid explore-detail-span">
                <strong>Relationships</strong>
                {(selectedNode.value.relationships ?? []).length > 0 ? (
                  <ul className="explore-relationship-list">
                    {(selectedNode.value.relationships ?? []).map((relationship, index) => (
                      <li key={`${relationship.otherEntityId || index}-${relationship.relateType}-${relationship.direction}`}>
                        <strong>{relationship.relateType || 'Related To'}</strong>{' '}
                        {relationship.otherEntityName || relationship.otherEntityTitle || relationship.otherEntityId || 'Unknown'}{' '}
                        <span className="existing-list-title">
                          ({relationship.otherEntityType || 'Entity'}, {relationship.direction || 'unknown'})
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div>No loaded being/place relationships.</div>
                )}
              </div>
            </div>
          )}

          {selectedNode.type === 'item' && (
            <div className="explore-detail-grid">
              <div className="empty-grid">
                <strong>{getItemLabel(selectedNode.value)}</strong>
                <div>{selectedNode.value.title || 'No title'}</div>
              </div>
              <div className="empty-grid">
                <strong>Type</strong>
                <div>{[selectedNode.value.itemType, selectedNode.value.level !== '' ? `Lvl ${selectedNode.value.level}` : ''].filter(Boolean).join(' / ') || 'Unknown'}</div>
              </div>
              <div className="empty-grid">
                <strong>Owner</strong>
                <div>{selectedNode.value.ownerType ? `${selectedNode.value.ownerType}: ${selectedNode.value.ownerId || 'Unknown'}` : 'Unknown'}</div>
              </div>
              <div className="empty-grid">
                <strong>Value</strong>
                <div>{formatGpValue(selectedNode.value.value)}</div>
              </div>
              <div className="empty-grid explore-detail-span">
                <strong>Description</strong>
                <div>{selectedNode.value.description || 'No description.'}</div>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default Explore;
